import React, { useState } from "react";
import { 
  Settings, 
  Eye, 
  Plus, 
  Trash2, 
  Palette, 
  Maximize2, 
  Type,
  Layout as LayoutIcon,
  ChevronRight, 
  RefreshCcw, 
  Save
} from "lucide-react";

const FooterAdminPanel = () => {
  const [footer, setFooter] = useState({
    bgColor: "#0f172a",
    textColor: "#94a3b8", // Slate-400 for a softer look
    width: 1100,
    height: 280,
    items: ["Home", "Solutions", "Privacy", "Contact"],
  });

  const [newItem, setNewItem] = useState("");
   const [isSaving, setIsSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFooter({ ...footer, [name]: value });
  };

  const addItem = () => {
    if (newItem.trim() === "") return;
    setFooter({ ...footer, items: [...footer.items, newItem] });
    setNewItem("");
  };

  const deleteItem = (index) => {
    setFooter({ ...footer, items: footer.items.filter((_, i) => i !== index) });
  };

  const editItem = (index, value) => {
    const updated = [...footer.items];
    updated[index] = value;
    setFooter({ ...footer, items: updated });
  };

  //  const handleSave = () => {
  //   setIsSaving(true);
  //   setTimeout(() => {
  //     setIsSaving(false);
  //     alert("Theme Saved:", theme);
  //   }, 1500);
  // };


  return (
    <div className="min-h-screen bg-[#020617] text-slate-200 p-6 md:p-12 font-sans">
      
      {/* Header Area */}
      <div className="max-w-7xl mx-auto mb-12 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
        
          <h1 className="text-4xl font-black tracking-tight text-white">Footer <span className="text-blue-500">Builder</span></h1>
        </div>
                <div className="flex items-center gap-6">
            {/* Reset Button */}
            <button
              onClick={() => setTheme(defaultTheme)}
              className="flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-white transition-colors uppercase tracking-widest"
            >
              <RefreshCcw size={14} /> Reset
            </button>

            {/* Professional Save Button */}
            <button
              // onClick={handleSave}
              // disabled={isSaving}
              className={`group relative flex items-center justify-center gap-3 px-8 py-3 rounded-xl font-bold transition-all active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed ${
                isSaving
                  ? "bg-emerald-600 shadow-[0_0_20px_rgba(16,185,129,0.3)]"
                  : "bg-blue-600 hover:bg-blue-500 shadow-[0_0_20px_rgba(37,99,235,0.3)]"
              } text-white`}
            >
              {/* {isSaving ? (
                <RefreshCcw size={18} className="animate-spin text-white" />
              ) : (
                <Save
                  size={18}
                  className="group-hover:translate-y-[-1px] transition-transform"
                />
              )} */}

              <span className="text-sm tracking-wide">
                {isSaving ? "Publishing..." : "Save Changes"}
              </span>

              {/* Glass Inner Border */}
              <div className="absolute inset-0 rounded-xl border border-white/10 pointer-events-none"></div>
            </button>
          </div>
      </div>

      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10">

        {/* --- LEFT: SETTINGS (5 Cols) --- */}
        <div className="lg:col-span-5 space-y-8">
          
          {/* Section: Appearance */}
          <section className="bg-slate-900/50 border border-slate-800 p-8 rounded-3xl backdrop-blur-sm">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-blue-500/10 rounded-lg text-blue-500">
                <Palette size={20} />
              </div>
              <h2 className="text-xl font-bold text-white">Canvas Styling</h2>
            </div>

            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="space-y-3">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Background</label>
                <div className="flex items-center gap-3 bg-slate-800/50 p-2 rounded-2xl border border-slate-700">
                  <input type="color" name="bgColor" value={footer.bgColor} onChange={handleChange} className="w-10 h-10 rounded-xl cursor-pointer bg-transparent border-none" />
                  <span className="text-sm font-mono text-slate-400">{footer.bgColor}</span>
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Typography</label>
                <div className="flex items-center gap-3 bg-slate-800/50 p-2 rounded-2xl border border-slate-700">
                  <input type="color" name="textColor" value={footer.textColor} onChange={handleChange} className="w-10 h-10 rounded-xl cursor-pointer bg-transparent border-none" />
                  <span className="text-sm font-mono text-slate-400">{footer.textColor}</span>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div className="space-y-4">
                <div className="flex justify-between text-xs font-bold uppercase tracking-widest text-slate-500">
                  <span>Width Control</span>
                  <span className="text-blue-500">{footer.width}px</span>
                </div>
                <input type="range" min="600" max="1400" name="width" value={footer.width} onChange={handleChange} className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-blue-500" />
              </div>

              <div className="space-y-4">
                <div className="flex justify-between text-xs font-bold uppercase tracking-widest text-slate-500">
                  <span>Height Control</span>
                  <span className="text-blue-500">{footer.height}px</span>
                </div>
                <input type="range" min="150" max="450" name="height" value={footer.height} onChange={handleChange} className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-blue-500" />
              </div>
            </div>
          </section>

          {/* Section: Navigation Management */}
          <section className="bg-slate-900/50 border border-slate-800 p-8 rounded-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-indigo-500/10 rounded-lg text-indigo-500">
                <Type size={20} />
              </div>
              <h2 className="text-xl font-bold text-white">Menu Items</h2>
            </div>

            <div className="flex gap-2 mb-6">
              <input
                type="text"
                placeholder="Link Title (e.g. Careers)"
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                className="flex-1 bg-slate-800 border border-slate-700 p-3 rounded-xl outline-none focus:border-blue-500 transition-all text-sm"
              />
              <button onClick={addItem} className="bg-slate-200 hover:bg-white text-black p-3 rounded-xl transition-all">
                <Plus size={20} />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 max-h-[250px] overflow-y-auto pr-2 custom-scrollbar">
              {footer.items.map((item, index) => (
                <div key={index} className="group flex items-center gap-3 bg-slate-800/30 border border-slate-700/50 p-3 rounded-xl hover:bg-slate-800/60 transition-all">
                  <div className="text-slate-600 group-hover:text-blue-500 transition-colors">
                    <ChevronRight size={16} />
                  </div>
                  <input
                    value={item}
                    onChange={(e) => editItem(index, e.target.value)}
                    className="flex-1 bg-transparent outline-none text-sm font-medium"
                  />
                  <button onClick={() => deleteItem(index)} className="text-slate-600 hover:text-red-400 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* --- RIGHT: PREVIEW (7 Cols) --- */}
        <div className="lg:col-span-7">
          <div className="sticky top-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-emerald-500/10 rounded-lg text-emerald-500">
                <Eye size={20} />
              </div>
              <h2 className="text-xl font-bold text-white">Live Viewport</h2>
            </div>

            {/* Browser Frame Emulator */}
            <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-2xl">
              <div className="bg-slate-800/50 p-4 border-b border-slate-700 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/50" />
                  <div className="w-3 h-3 rounded-full bg-amber-500/20 border border-amber-500/50" />
                  <div className="w-3 h-3 rounded-full bg-emerald-500/20 border border-emerald-500/50" />
                </div>
                <div className="mx-auto bg-slate-900 px-10 py-1 rounded-md text-[10px] text-slate-500 font-mono">
                  suraksha24x7.com/preview
                </div>
              </div>

              <div className="p-8 bg-white/5 min-h-[500px] flex items-end justify-center overflow-x-auto">
                <footer
                  style={{
                    backgroundColor: footer.bgColor,
                    color: footer.textColor,
                    minHeight: `${footer.height}px`,
                    width: `${footer.width}px`,
                  }}
                  className="rounded-2xl p-10 flex flex-col justify-between shadow-2xl transition-all duration-300 ease-out"
                >
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-2xl font-black italic tracking-tighter mb-4" style={{ color: '#fff' }}>
                        SURAKSHA<span className="text-blue-500">24x7</span>
                      </h3>
                      <p className="text-xs leading-relaxed opacity-70 max-w-xs">
                        Advanced security solutions for a safer tomorrow. Integrating AI and human expertise.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <h4 className="text-[10px] font-bold uppercase tracking-widest text-white/50 mb-2">Explore</h4>
                        <div className="flex flex-col gap-2">
                          {footer.items.map((item, index) => (
                            <span key={index} className="text-xs hover:text-white cursor-pointer transition-colors">
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h4 className="text-[10px] font-bold uppercase tracking-widest text-white/50 mb-2">Social</h4>
                        <div className="flex flex-col gap-2 text-xs">
                          <span>Twitter</span>
                          <span>LinkedIn</span>
                          <span>Instagram</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-10 pt-6 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
                    <p className="text-[10px] uppercase tracking-widest opacity-40">
                      © 2026 SURAKSHA 24X7. NO RIGHTS RESERVED.
                    </p>
                    <div className="flex gap-4 text-[10px] font-bold text-blue-500 uppercase tracking-tighter">
                      <span>Terms</span>
                      <span>Privacy</span>
                      <span>Cookies</span>
                    </div>
                  </div>
                </footer>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="mt-6 grid grid-cols-3 gap-4">
              <div className="bg-slate-900 border border-slate-800 p-4 rounded-2xl text-center">
                <span className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Total Links</span>
                <span className="text-xl font-black text-white">{footer.items.length}</span>
              </div>
              <div className="bg-slate-900 border border-slate-800 p-4 rounded-2xl text-center">
                <span className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Contrast</span>
                <span className="text-xl font-black text-emerald-500">Pass</span>
              </div>
              <div className="bg-slate-900 border border-slate-800 p-4 rounded-2xl text-center">
                <span className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Height</span>
                <span className="text-xl font-black text-blue-500">{footer.height}</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default FooterAdminPanel;